<!--
  Button Component
  Variants: primary (default), secondary, ghost, danger
  Sizes: sm, md (default), lg
  Features: icon-only, loading state, disabled
-->
<script lang="ts">
  export let variant: 'primary' | 'secondary' | 'ghost' | 'danger' = 'primary';
  export let size: 'sm' | 'md' | 'lg' = 'md';
  export let type: 'button' | 'submit' | 'reset' = 'button';
  export let disabled = false;
  export let loading = false;
  export let iconOnly = false;
  export let fullWidth = false;
  export let href: string | undefined = undefined;
  // Extra caller classes (e.g. a one-off color like `text-error`) — merged in, not overridden by
  // the base/variant/size classes below. Declaring `class` explicitly keeps it out of
  // `$$restProps`, which would otherwise clobber the whole `classes` attribute (F10, Spec 086).
  let className = '';
  export { className as class };

  const baseClasses =
    'inline-flex items-center justify-center font-medium transition-colors duration-150 cursor-pointer border-none';

  const variantClasses: Record<string, string> = {
    primary: 'bg-accent text-on-accent hover:bg-accent-hover',
    secondary: 'bg-surface text-text border border-border hover:bg-surface-hover',
    ghost: 'bg-transparent text-text-secondary hover:bg-surface hover:text-text',
    danger: 'bg-error text-white hover:bg-red-600',
  };

  const sizeClasses: Record<string, string> = {
    sm: iconOnly ? 'w-8 h-8 rounded' : 'h-8 px-3 text-sm rounded gap-1.5',
    md: iconOnly ? 'w-9 h-9 rounded' : 'h-9 px-4 text-base rounded gap-2',
    lg: iconOnly ? 'w-10 h-10 rounded-lg' : 'h-10 px-5 text-lg rounded-lg gap-2',
  };

  $: classes = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth && 'w-full',
    (disabled || loading) && 'opacity-50 cursor-not-allowed pointer-events-none',
    className,
  ]
    .filter(Boolean)
    .join(' ');
</script>

{#if href && !disabled}
  <a {href} class={classes} on:click {...$$restProps}>
    {#if loading}
      <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"
        ></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        ></path>
      </svg>
    {/if}
    <slot />
  </a>
{:else}
  <button {type} {disabled} class={classes} on:click {...$$restProps}>
    {#if loading}
      <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"
        ></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        ></path>
      </svg>
    {/if}
    <slot />
  </button>
{/if}
